import { useEffect } from 'react';

import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';

import { SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT } from '../analytics.ts';
import { isViewModeSuggestionsEnabled } from '../isViewModeSuggestionsEnabled.ts';
import { useShowSuggestionsPanel } from '../useShowSuggestionsPanel.ts';

import {
	getPublishedSuggestionsSnapshot,
	invalidatePublishedSuggestionsCache,
	resetPublishedSuggestions,
} from './publishedSuggestionsStore.ts';
import { usePublishedSuggestionsState } from './usePublishedSuggestionsState.ts';

export const useSuggestionsPanelEditHandoff = (contentId: string | undefined): boolean => {
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const { isSuggestionsPanelShown } = useShowSuggestionsPanel();
	const { contentId: publishedContentId } = usePublishedSuggestionsState();

	// Unlike the panel, a missing editor doesn't mean published mode: the editor may still be loading.
	const isPublishedMode = publishedContentId !== undefined && isViewModeSuggestionsEnabled();
	const isEditHandoffPending =
		isPublishedMode && isSuggestionsPanelShown && !!contentId && publishedContentId === contentId;

	useEffect(() => {
		if (!contentId || !isViewModeSuggestionsEnabled()) {
			return;
		}
		invalidatePublishedSuggestionsCache(contentId);
		return () => invalidatePublishedSuggestionsCache(contentId);
	}, [contentId]);

	useEffect(() => {
		if (isPublishedMode && !isEditHandoffPending) {
			resetPublishedSuggestions();
		}
	}, [isEditHandoffPending, isPublishedMode]);

	useEffect(() => {
		const aiSuggestions = fullPageEditorPresetAPI?.aiSuggestions;
		if (!isEditHandoffPending || !aiSuggestions) {
			return;
		}

		let isDone = false;
		let unsubscribeSuggestions: (() => void) | undefined;

		const finish = () => {
			isDone = true;
			unsubscribeSuggestions?.();
			resetPublishedSuggestions();
		};

		const selectViewedSuggestion = (suggestionId: string): boolean => {
			if (!aiSuggestions.sharedState.currentState()?.suggestionById.has(suggestionId)) {
				return false;
			}
			aiSuggestions.actions.selectSuggestion(suggestionId, { interactionPoint: 'sidebar' });
			return true;
		};

		const completeHandoff = async () => {
			const { selectedSuggestionId, shouldStartReviewInEditMode } =
				getPublishedSuggestionsSnapshot();

			if (selectedSuggestionId && !shouldStartReviewInEditMode) {
				if (selectViewedSuggestion(selectedSuggestionId)) {
					finish();
					return;
				}
				unsubscribeSuggestions = aiSuggestions.sharedState.onChange(() => {
					if (!isDone && selectViewedSuggestion(selectedSuggestionId)) {
						finish();
					}
				});
			}

			try {
				await aiSuggestions.actions.refreshSuggestions();
			} catch {}

			if (isDone) {
				return;
			}

			if (selectedSuggestionId) {
				selectViewedSuggestion(selectedSuggestionId);
			}

			if (shouldStartReviewInEditMode) {
				aiSuggestions.actions.setThinkingState('thinking', {
					entryPoint: SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT,
				});
			}

			finish();
		};

		void completeHandoff();

		return () => {
			isDone = true;
			unsubscribeSuggestions?.();
		};
	}, [fullPageEditorPresetAPI, isEditHandoffPending]);

	return isEditHandoffPending;
};
